import React, { useState } from 'react';
import { DataGridPro, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid-pro';
import { format, getISOWeek } from 'date-fns';
import { da } from 'date-fns/locale';
import { Box, Button, capitalize, Checkbox, CheckboxProps, Popover, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarClock, faCalendarDays, faCheck, faClock } from '@fortawesome/pro-light-svg-icons';
import { amber } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import { CitizenLessonDeviationDto, CourseEnrollmentLessonDto } from '../../../../api-service/clients';
import { formatDate } from '../../../../utilities';
import { DatePickerValue } from '../../../../components/date-picker';
import { getLessonStateDisplayValue } from '../../utils';
import { FasitDataGridToolbar } from '../../../../components/data-grid/fasit-data-grid-toolbar';
import { CitizenLessonDeviationWeekdaySelector } from './citizen-lesson-deviation-weekday-selector';

export interface CitizenLessonDeviationRepetitionRow extends CourseEnrollmentLessonDto {
  deviation?: CitizenLessonDeviationDto;
}

interface ICitizenLessonDeviationRepetitionsProps {
  lessons: CitizenLessonDeviationRepetitionRow[];
  selectedLessonIds: string[];
  currentLessonId?: string;
  onSelectionChange: (lessonIds: string[]) => void;
  onSelectSameTimeWindow: () => void;
  onSelectDeviationsInSameTimeWindow: () => void;
  onApplyWeekdays: (weekdays: number[], fromDate: DatePickerValue, toDate: DatePickerValue) => void;
}

export function CitizenLessonDeviationRepetitions(props: ICitizenLessonDeviationRepetitionsProps) {
  const { lessons, selectedLessonIds, currentLessonId, onSelectionChange, onSelectSameTimeWindow, onSelectDeviationsInSameTimeWindow, onApplyWeekdays } = props;

  function handleSelectionChange(selectionModel: GridSelectionModel) {
    onSelectionChange(selectionModel.map(id => id.toString()));
  }

  function isCurrentLesson(lesson: CitizenLessonDeviationRepetitionRow) {
    return lesson.lessonId.toLowerCase() === currentLessonId?.toLowerCase();
  }

  function getRowClassName(lesson: CitizenLessonDeviationRepetitionRow) {
    return [lesson.deviation ? 'has-deviation-row' : '', isCurrentLesson(lesson) ? 'current-lesson-row' : ''].filter(Boolean).join(' ');
  }

  return (
    <DataGridPro<CitizenLessonDeviationRepetitionRow>
      columns={columns}
      rows={lessons}
      sx={theme => ({
        height: 'calc(100vh - 720px)',
        minHeight: 240,
        '& .MuiCheckbox-root.Mui-disabled': { color: 'action.disabled' },
        '& .has-deviation-row': { boxShadow: `inset 4px 0 0 ${amber[700]}` },
        '& .current-lesson-row': { backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity) },
        '& .current-lesson-row:hover': {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
        },
      })}
      density="compact"
      getRowId={lesson => lesson.lessonId}
      getRowClassName={({ row }) => getRowClassName(row)}
      initialState={{ sorting: { sortModel: [{ field: 'beginAt', sort: 'asc' }] } }}
      checkboxSelection
      disableSelectionOnClick
      hideFooter
      isRowSelectable={({ row }) => !isCurrentLesson(row)}
      selectionModel={selectedLessonIds}
      onSelectionModelChange={handleSelectionChange}
      components={{
        Toolbar: RepetitionsToolbar,
        BaseCheckbox: RepetitionCheckbox,
      }}
      componentsProps={{
        toolbar: { onSelectSameTimeWindow, onSelectDeviationsInSameTimeWindow, onApplyWeekdays },
        panel: {
          disablePortal: true,
          placement: 'bottom-end',
        },
      }}
    />
  );
}

function RepetitionCheckbox(props: CheckboxProps) {
  if (!props.disabled) {
    return <Checkbox {...props} />;
  }
  return (
    <Tooltip title="Valgte særplan">
      <span>
        <Checkbox {...props} />
      </span>
    </Tooltip>
  );
}

function RepetitionsToolbar({
  onSelectSameTimeWindow,
  onSelectDeviationsInSameTimeWindow,
  onApplyWeekdays,
}: Pick<ICitizenLessonDeviationRepetitionsProps, 'onSelectSameTimeWindow' | 'onSelectDeviationsInSameTimeWindow' | 'onApplyWeekdays'>) {
  const [weekdayAnchorEl, setWeekdayAnchorEl] = useState<HTMLElement | null>(null);

  function handleOpenWeekdayPopover(event: React.MouseEvent<HTMLElement>) {
    setWeekdayAnchorEl(event.currentTarget);
  }

  function handleCloseWeekdayPopover() {
    setWeekdayAnchorEl(null);
  }

  function handleApplyWeekdays(weekdays: number[], fromDate: DatePickerValue, toDate: DatePickerValue) {
    onApplyWeekdays(weekdays, fromDate, toDate);
    handleCloseWeekdayPopover();
  }

  return FasitDataGridToolbar({
    title: 'Lektioner',
    customInput: (
      <>
        <Tooltip title="Vælg lektioner med samme tidsrum som særplanen">
          <Button
            aria-label="Vælg lektioner med samme tidsrum som særplanen"
            startIcon={<FontAwesomeIcon icon={faClock} />}
            onClick={onSelectSameTimeWindow}
            sx={{ ml: 'auto', py: '4px', px: '5px' }}
          />
        </Tooltip>
        <Tooltip title="Vælg særplaner med samme tidsrum som særplanen">
          <Button
            aria-label="Vælg særplaner med samme tidsrum som særplanen"
            startIcon={<FontAwesomeIcon icon={faCalendarClock} />}
            onClick={onSelectDeviationsInSameTimeWindow}
            sx={{ py: '4px', px: '5px' }}
          />
        </Tooltip>
        <Tooltip title="Vælg lektioner efter ugedag og periode">
          <Button
            aria-label="Vælg lektioner efter ugedag og periode"
            startIcon={<FontAwesomeIcon icon={faCalendarDays} />}
            onClick={handleOpenWeekdayPopover}
            sx={{ py: '4px', px: '5px' }}
          />
        </Tooltip>
        <Popover
          open={Boolean(weekdayAnchorEl)}
          anchorEl={weekdayAnchorEl}
          onClose={handleCloseWeekdayPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <CitizenLessonDeviationWeekdaySelector onApply={handleApplyWeekdays} />
        </Popover>
      </>
    ),
  });
}

function DeviationCell({ lesson }: { lesson: CitizenLessonDeviationRepetitionRow }) {
  const { deviation } = lesson;

  if (!deviation) {
    return null;
  }

  return (
    <Box title={deviation.isExemptFromLesson ? 'Særplan, fritaget' : 'Særplan'}>
      <FontAwesomeIcon icon={faCheck} />
    </Box>
  );
}

const columns: GridColDef[] = [
  {
    headerName: 'Dato',
    field: 'beginAt',
    width: 150,
    valueFormatter: params => formatDate(params.value),
  },
  {
    headerName: 'Ugedag',
    field: 'weekday',
    width: 100,
    valueGetter: ({ row }) => capitalize(format(new Date(row.beginAt), 'EEEE', { locale: da })),
  },
  {
    headerName: 'Uge',
    field: 'weekNumber',
    width: 80,
    valueGetter: ({ row }) => getISOWeek(new Date(row.beginAt)),
  },
  {
    headerName: 'Fra',
    field: 'startTime',
    width: 90,
    valueGetter: ({ row }) => formatDate(row.deviation?.beginAt ?? row.beginAt, 'HH:mm'),
  },
  {
    headerName: 'Til',
    field: 'endAt',
    width: 90,
    valueGetter: ({ row }) => formatDate(row.deviation?.endAt ?? row.endAt, 'HH:mm'),
  },
  {
    headerName: 'Lokale',
    field: 'classroomName',
    flex: 1,
  },
  {
    headerName: 'Særplan',
    field: 'hasDeviation',
    width: 100,
    type: 'boolean',
    valueGetter: ({ row }) => Boolean(row.deviation),
    renderCell: (params: GridRenderCellParams) => <DeviationCell lesson={params.row} />,
  },
  {
    headerName: 'Status',
    field: 'lessonState',
    width: 120,
    valueGetter: ({ row }) => getLessonStateDisplayValue(row.lessonState),
  },
];
